import { createSlice } from "@reduxjs/toolkit";

const initialShippingState = {
  listLoading: false,
  actionsLoading: false,
  integrationStatus: null,
  carriers: [],
  ltlCarriers: [],
  ltlCarriersLoading: false,
  ltlCredentialRequirements: null,
  ltlQuotes: [],
  ltlQuoteErrors: [],
  ltlQuotesLoading: false,
  selectedLTLQuote: null,
  ltlRateConfirmed: false,
  ltlSkippedQuotes: false,
  ltlConfirmedCarrier: null,
  ltlShipmentPayload: null,
  ltlShippingOptions: null,
  ltlPickupLoading: false,
  ltlPickupResult: null,
  ltlPickupError: null,
  ltlBolLoading: false,
  ltlTrackingInfo: null,
  ltlTrackingLoading: false,
  elementsJwt: null,
  carrierSyncResult: null,
  // NEW state
  rates: [],
  invalidRates: [],
  addressValidation: null,
  shipmentId: null,
  rateRequestId: null,
  labelResult: null,
  labelDirectResult: null,
  trackingInfo: null,
  voidResult: null,
  orderShipments: null,
  readyToShipEntities: [],
  readyToShipTotalCount: 0,
  readyToShipLoading: false,
  shippedEntities: [],
  shippedTotalCount: 0,
  shippedLoading: false,
  message: {
    error: null,
    success: null,
  },
};

export const callTypes = {
  list: "list",
  actions: "actions",
};

export const shippingSlice = createSlice({
  name: "shipping",
  initialState: initialShippingState,
  reducers: {
    catchError: (state, action) => {
      const err = action.payload.error;
      if (err.response && err.response.data) {
        const data = err.response.data;
        const errors = Array.isArray(data.errors) ? data.errors : [];
        state.message.error = data.message ? [data.message, ...errors] : errors.length ? errors : [err.message];
      } else {
        state.message.error = err.message ? [err.message] : ["An error occurred"];
      }
      if (callTypes[action.payload.callType]) {
        state[`${action.payload.callType}Loading`] = false;
      }
    },

    startCall: (state, action) => {
      state.message.error = null;
      if (callTypes[action.payload.callType]) {
        state[`${action.payload.callType}Loading`] = true;
      }
    },

    // ── EXISTING REDUCERS ──

    integrationStatusFetched: (state, action) => {
      state.listLoading = false;
      state.message.error = null;
      state.integrationStatus = action.payload;
    },

    accountCreated: (state, action) => {
      const { message, seller_id, onboarding_status } = action.payload;
      state.actionsLoading = false;
      state.message.error = null;
      state.message.success = message;
      state.integrationStatus = {
        ...state.integrationStatus,
        is_connected: true,
        seller_id,
        onboarding_status,
      };
    },

    onboardingStatusUpdated: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.integrationStatus = action.payload;
      state.message.success = "Onboarding status updated";
    },

    jwtFetched: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.elementsJwt = action.payload;
    },

    carriersFetched: (state, action) => {
      state.listLoading = false;
      state.message.error = null;
      state.carriers = action.payload.carriers || [];
    },

    carriersSynced: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.carrierSyncResult = action.payload;
      state.carriers = action.payload.carriers || state.carriers;
      state.message.success = action.payload.message || "Carriers synced";
    },

    // ── NEW REDUCERS ──

    ratesFetched: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.rates = action.payload.rates || [];
      state.invalidRates = action.payload.invalid_rates || [];
      state.shipmentId = action.payload.shipment_id;
      state.rateRequestId = action.payload.rate_request_id;
    },

    ratesCleared: (state) => {
      state.rates = [];
      state.invalidRates = [];
      state.shipmentId = null;
      state.rateRequestId = null;
    },

    addressesValidated: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.addressValidation = action.payload;
    },

    addressValidationCleared: (state) => {
      state.addressValidation = null;
    },

    labelPurchased: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.labelResult = action.payload;
      state.message.success = "Shipping label purchased successfully";
    },

    labelDirectPurchased: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.labelDirectResult = action.payload;
      state.message.success = `${action.payload.label_count} label(s) purchased successfully`;
    },

    trackingFetched: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.trackingInfo = action.payload;
    },

    labelVoided: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.voidResult = action.payload;
      state.message.success = action.payload.approved
        ? "Label voided successfully"
        : "Label void request submitted";
    },

    // ── UTILITY REDUCERS ──

    updateMessage: (state, action) => {
      const { type, message } = action.payload;
      state.message[type] = message;
      state.actionsLoading = false;
    },

    exceptionHandlerCleaner: (state) => {
      state.listLoading = false;
      state.actionsLoading = false;
    },

    resetShippingWizard: (state) => {
      state.rates = [];
      state.invalidRates = [];
      state.shipmentId = null;
      state.rateRequestId = null;
      state.labelResult = null;
      state.labelDirectResult = null;
      state.message.error = null;
      state.message.success = null;
    },
     orderShipmentsFetched: (state, action) => {
      state.listLoading = false;
      state.message.error = null;
      state.orderShipments = action.payload;
    },

    // ── READY TO SHIP REDUCERS ──

    readyToShipLoading: (state) => {
      state.readyToShipLoading = true;
      state.message.error = null;
    },

    readyToShipOrdersFetched: (state, action) => {
      const { totalCount, entities } = action.payload;
      state.readyToShipLoading = false;
      state.message.error = null;
      state.readyToShipEntities = entities;
      state.readyToShipTotalCount = totalCount;
    },

    // ── SHIPPED REDUCERS ──

    shippedLoading: (state) => {
      state.shippedLoading = true;
      state.message.error = null;
    },

    shippedOrdersFetched: (state, action) => {
      const { totalCount, entities } = action.payload;
      state.shippedLoading = false;
      state.message.error = null;
      state.shippedEntities = entities;
      state.shippedTotalCount = totalCount;
    },

    readyToShipLoadingStopped: (state) => {
      state.readyToShipLoading = false;
    },

    shippedLoadingStopped: (state) => {
      state.shippedLoading = false;
    },

    // ── LTL CARRIER REDUCERS ──

    ltlCarriersLoading: (state) => {
      state.ltlCarriersLoading = true;
    },
    ltlCarriersFetched: (state, action) => {
      state.listLoading = false;
      state.ltlCarriersLoading = false;
      state.message.error = null;
      state.ltlCarriers = action.payload.carriers || [];
    },

    ltlCarriersSynced: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.message.success = action.payload.message || "LTL carriers synced";
    },

    ltlCarrierConnected: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.message.success = action.payload.message || "LTL carrier connected";
    },

    ltlCarrierStatusUpdated: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      state.message.success = action.payload.message || "Carrier status updated";
      const { carrier_id, carrier_status } = action.payload;
      const idx = state.ltlCarriers.findIndex((c) => c.carrier_id === carrier_id);
      if (idx !== -1) {
        state.ltlCarriers[idx].carrier_status = carrier_status;
      }
    },

    ltlCredentialRequirementsFetched: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      const reqs = action.payload.credential_requirements;
      state.ltlCredentialRequirements = reqs === null ? null : reqs || [];
    },

    ltlShippingOptionsFetched: (state, action) => {
      state.listLoading = false;
      state.message.error = null;
      state.ltlShippingOptions = action.payload;
    },

    // ── LTL QUOTE REDUCERS ──

    ltlQuotesLoading: (state) => {
      state.ltlQuotesLoading = true;
      state.ltlQuotes = [];
      state.ltlQuoteErrors = [];
      state.selectedLTLQuote = null;
      state.message.error = null;
    },

    ltlQuotesFetched: (state, action) => {
      state.ltlQuotesLoading = false;
      state.message.error = null;
      state.ltlQuotes = action.payload.quotes || [];
      state.ltlQuoteErrors = action.payload.errors || [];
    },

    ltlQuoteSelected: (state, action) => {
      state.selectedLTLQuote = action.payload;
    },

    ltlQuotesCleared: (state) => {
      state.ltlQuotes = [];
      state.ltlQuoteErrors = [];
      state.ltlQuotesLoading = false;
      state.selectedLTLQuote = null;
      state.ltlRateConfirmed = false;
      state.ltlSkippedQuotes = false;
      state.ltlConfirmedCarrier = null;
      state.ltlShipmentPayload = null;
    },

    // ── LTL RATE CONFIRMATION REDUCERS ──

    ltlRateConfirmedAction: (state) => {
      state.ltlRateConfirmed = true;
    },

    ltlQuotesSkipped: (state, action) => {
      state.ltlSkippedQuotes = true;
      state.ltlShipmentPayload = action.payload || null;
    },

    ltlSkipCarrierSelected: (state, action) => {
      state.ltlConfirmedCarrier = action.payload;
    },

    ltlRateConfirmationCleared: (state) => {
      state.ltlRateConfirmed = false;
      state.ltlSkippedQuotes = false;
      state.selectedLTLQuote = null;
      state.ltlConfirmedCarrier = null;
      state.ltlShipmentPayload = null;
    },

    // ── LTL PICKUP REDUCERS ──

    ltlPickupLoading: (state) => {
      state.ltlPickupLoading = true;
      state.ltlPickupResult = null;
      state.ltlPickupError = null;
      state.message.error = null;
    },

    ltlPickupScheduled: (state, action) => {
      state.ltlPickupLoading = false;
      state.ltlPickupResult = action.payload;
      state.ltlPickupError = null;
      state.message.success = action.payload.message || "LTL pickup scheduled";
    },

    ltlPickupFailed: (state, action) => {
      state.ltlPickupLoading = false;
      state.ltlPickupError = action.payload;
    },

    ltlBolLoading: (state) => {
      state.ltlBolLoading = true;
    },

    ltlBolRegenerated: (state, action) => {
      state.ltlBolLoading = false;
      if (state.ltlPickupResult) {
        state.ltlPickupResult.bol_document = action.payload.bol_document;
        state.ltlPickupResult.documents = action.payload.documents;
      }
    },

    ltlBolFailed: (state) => {
      state.ltlBolLoading = false;
    },

    ltlPickupCleared: (state) => {
      state.ltlPickupLoading = false;
      state.ltlPickupResult = null;
      state.ltlPickupError = null;
      state.ltlBolLoading = false;
    },

    // ── LTL TRACKING REDUCERS ──

    ltlTrackingLoading: (state) => {
      state.ltlTrackingLoading = true;
      state.ltlTrackingInfo = null;
    },

    ltlTrackingFetched: (state, action) => {
      state.ltlTrackingLoading = false;
      state.ltlTrackingInfo = action.payload;
    },

    ltlTrackingFailed: (state) => {
      state.ltlTrackingLoading = false;
      state.ltlTrackingInfo = null;
    },

    ltlTrackingCleared: (state) => {
      state.ltlTrackingLoading = false;
      state.ltlTrackingInfo = null;
    },

    trackingCleared: (state) => {
      state.trackingInfo = null;
    },
  },
});
